import { useQuery, UseQueryResult, useMutation, useQueryClient } from '@tanstack/react-query'
import { usersApi } from '../api/users'
import type { User } from '../types'
import { useTelegram } from './useTelegram'
import { AxiosError } from 'axios'

interface UseUserOptions {
  enabled?: boolean
  autoRegister?: boolean
}

export const useUser = (
  telegramId: number | undefined,
  options?: UseUserOptions
): UseQueryResult<User, Error> => {
  const { user: telegramUser } = useTelegram()
  const queryClient = useQueryClient()

  // Auto-registration mutation
  const registerMutation = useMutation({
    mutationFn: async () => {
      if (!telegramUser) throw new Error('Telegram user data not available')

      return usersApi.registerUser({
        telegram_id: telegramUser.id,
        username: telegramUser.username,
        first_name: telegramUser.first_name,
        last_name: telegramUser.last_name,
        language_code: telegramUser.language_code,
      })
    },
    onSuccess: (newUser) => {
      // Update the cache with the newly registered user
      queryClient.setQueryData(['user', telegramId], newUser)
    },
  })

  return useQuery({
    queryKey: ['user', telegramId],
    queryFn: async () => {
      if (!telegramId) throw new Error('Telegram ID is required')

      try {
        return await usersApi.getUserByTelegramId(telegramId)
      } catch (error) {
        // If user not found (404) and auto-registration is enabled, register the user
        if (
          error instanceof AxiosError &&
          error.response?.status === 404 &&
          options?.autoRegister !== false &&
          telegramUser
        ) {
          console.log('[useUser] User not found, auto-registering...')
          const newUser = await registerMutation.mutateAsync()
          return newUser
        }
        throw error
      }
    },
    enabled: options?.enabled !== false && !!telegramId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    retry: (failureCount, error) => {
      // Don't retry on 404 if auto-register is enabled (we handle it in queryFn)
      if (error instanceof AxiosError && error.response?.status === 404) {
        return false
      }
      return failureCount < 3
    },
  })
}
